// libs
import { getExperimentVariant } from '@nykaa/experiment-sdk/client';

// helpers
import { ApiHelper } from 'shared/helpers/api';
import { getUrl, ROUTES } from 'shared/api/urls';
import { cartWidgetsTransformer, transformAddToCart, transformCart } from './transformer';
import { getDeviceType, getDomain } from 'shared/helpers/app';
import { getEddHeader, getONRHeader, getPincode, getTrackingParam } from './helpers';
import { log } from 'shared/helpers/newRelic';

// constants
import { API_ERROR_MESSAGE } from 'shared/constants/messages';
import { EXPERIMENT_CUSTOMISATION } from 'shared/api/cart/constants';
import { API_METHODS, METHODS } from 'shared/constants/api';
import { CART_ERROR_CODES } from 'shared/constants/cart';
import { EDD_SOURCE_PAGE } from 'shared/components/EstimatedDeliveryDetails/constants';
import { ERROR_TAGS } from 'shared/helpers/SentryLogger/constants';

// defs
import { ApiItem, ApiSizeItem, RewardUpdate } from 'shared/types/cart';
import { DEVICE_TYPE } from 'typings/cart';

export interface IAddToCart {
  sku: string;
  qty?: number;
  optionId?: string;
  optionValue?: string;
}

export interface IAddToCartItem {
  productId: string;
  quantity: number;
  productCustomAttributes?: Record<string, string> | null | undefined;
}

export interface IProductIdDTO {
  productId: string;
  cartItemId: string;
}

export type IAddToCartData =
  | {
      status: 'error';
      response: string;
    }
  | {
      status: 'fail';
      message: string;
    }
  | {
      status: 'success';
      response: { itemQuantity: number };
    };

export const settingShippingAddressForCartRules = (): Promise<any> => {
  const url = getUrl(ROUTES.MINI_CART);
  const data = new FormData();
  data.append('isAjax', '1');

  return ApiHelper(url, { method: 'POST', body: data }).then((_data: Response) => {
    return _data.json();
  });
};

export const addToCartV2 = ({
  items,
  deviceType,
}: {
  items: IAddToCartItem[];
  deviceType: DEVICE_TYPE;
}) => {
  const body = {
    items,
    deviceType,
    domain: 'NYKAA_FASHION',
    pincode: getPincode(),
    trackingParam: getTrackingParam(),
  };

  const url = getUrl(ROUTES.ADD_TO_CART_PLATFORM);

  const headers = { ...getEddHeader(), ...getONRHeader() };

  return ApiHelper(url, {
    method: API_METHODS[METHODS.POST],
    // @ts-ignore
    body,
    isBodyJSON: true,
    headers,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ statusCode: response.status });
    }

    const res = await response.json();

    if (res.statusCode !== '200') {
      return Promise.reject(res);
    }

    return {
      cartPartial: transformAddToCart(res.data),
      message: res.message,
    };
  });
};

export const addToCart = ({ items }: { items: ApiItem[] }) => {
  const body = {
    items,
    deviceType: getDeviceType(),
    domain: getDomain(),
    pincode: getPincode(),
  };

  const url = getUrl(ROUTES.ADD_TO_CART_PLATFORM);

  const headers = { ...getEddHeader(), ...getONRHeader() };

  return ApiHelper(url, {
    method: API_METHODS[METHODS.POST],
    body,
    isBodyJSON: true,
    headers,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.statusCode !== '200') {
      return Promise.reject(res);
    }

    return {
      cartPartial: transformAddToCart(res.data),
      message: res.message,
    };
  });
};

export const toggleRewardToCart = (reward: RewardUpdate, transactionType: 'apply' | 'remove') => {
  const body = {
    ...reward,
    deviceType: getDeviceType(),
    domain: getDomain(),
    pincode: getPincode(),
  };

  const url = getUrl(ROUTES.TOGGLE_CART_REWARD) + transactionType;

  const headers = { ...getEddHeader(), ...getONRHeader() };

  return ApiHelper(url, {
    method: transactionType === 'apply' ? API_METHODS[METHODS.POST] : API_METHODS[METHODS.DELETE],
    body,
    isBodyJSON: true,
    headers,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.statusCode !== '200') {
      return Promise.reject(res);
    }

    return { cart: transformCart(res.data), message: res.message as string };
  });
};

export const updateCartItems = ({ items }: { items: ApiItem[] }) => {
  const body = {
    items,
    deviceType: getDeviceType(),
    domain: getDomain(),
    pincode: getPincode(),
  };

  const url = getUrl(ROUTES.UPDATE_CART_ITEM);

  const headers = { ...getEddHeader(), ...getONRHeader() };

  return ApiHelper(url, {
    method: API_METHODS[METHODS.PATCH],
    body,
    isBodyJSON: true,
    headers,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.statusCode !== '200') {
      return Promise.reject(res);
    }

    return { cart: transformCart(res.data), message: res.message as string };
  });
};

export const deleteCartItem = (productIds: string[], productIdDTO?: IProductIdDTO[]) => {
  const { isEligible: isCustomisationEligible } = getExperimentVariant(EXPERIMENT_CUSTOMISATION);

  const body = isCustomisationEligible
    ? {
        productIds: productIds?.length ? productIds : null,
        productIdDTO: productIdDTO?.length ? productIdDTO : null,
        deviceType: getDeviceType(),
        domain: getDomain(),
        pincode: getPincode(),
      }
    : {
        productIds,
        deviceType: getDeviceType(),
        domain: getDomain(),
        pincode: getPincode(),
      };

  const url = getUrl(ROUTES.REMOVE_CART_ITEM);

  const headers = { ...getEddHeader(), ...getONRHeader() };

  return ApiHelper(url, {
    method: API_METHODS[METHODS.DELETE],
    body,
    isBodyJSON: true,
    headers,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.statusCode !== '200') {
      return Promise.reject(res);
    }

    return { cart: transformCart(res.data), message: res.message as string };
  });
};

export const replaceCartItemSize = ({ items }: { items: ApiSizeItem[] }) => {
  const body = {
    items,
    deviceType: getDeviceType(),
    domain: getDomain(),
    pincode: getPincode(),
  };

  const url = getUrl(ROUTES.REPLACE_CART_ITEM_SIZE);

  const headers = { ...getEddHeader(), ...getONRHeader() };

  return ApiHelper(url, {
    method: API_METHODS[METHODS.PATCH],
    body,
    isBodyJSON: true,
    headers,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.statusCode !== '200') {
      return Promise.reject(res);
    }

    return { cart: transformCart(res.data), message: res.message as string };
  });
};

export const getCartRefresh = ({ shouldSendPincode = false }) => {
  const baseURL = getUrl(ROUTES.GET_CART_REFRESH);
  const headers = { ...getEddHeader(), ...getONRHeader() };
  const params = new URLSearchParams();
  params.append('enable_pr', '1');
  const url = `${baseURL}?${params.toString()}`;

  const pincode = shouldSendPincode ? getPincode() : undefined;

  return ApiHelper(url, {
    method: API_METHODS[METHODS.POST],
    body: {
      deviceType: getDeviceType(),
      domain: getDomain(),
      pincode: pincode,
    },
    isBodyJSON: true,
    headers,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ statusCode: response?.status, message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    /**
     * 200 - valid cart
     * 003 - cart not found
     */
    if (res.statusCode !== '200' && res.statusCode !== '003') {
      return Promise.reject(res);
    }

    return { cart: transformCart(res.data), message: res.message as string };
  });
};

export const applyCoupon = (couponCode: string) => {
  const body = {
    couponCode,
    deviceType: getDeviceType(),
    domain: getDomain(),
    pincode: getPincode(),
  };

  const url = getUrl(ROUTES.APPLY_COUPON);

  const headers = { ...getEddHeader(), ...getONRHeader() };

  return ApiHelper(url, {
    method: API_METHODS[METHODS.POST],
    body,
    isBodyJSON: true,
    headers,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.statusCode !== '200') {
      return Promise.reject(res);
    }

    return { cart: transformCart(res.data), message: res.message as string };
  });
};

export const removeCoupon = () => {
  const body = {
    deviceType: getDeviceType(),
    domain: getDomain(),
    pincode: getPincode(),
  };

  const url = getUrl(ROUTES.REMOVE_COUPON);

  const headers = { ...getEddHeader(), ...getONRHeader() };

  return ApiHelper(url, {
    method: API_METHODS[METHODS.DELETE],
    body,
    isBodyJSON: true,
    headers,
  }).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.statusCode !== '200') {
      return Promise.reject(res);
    }

    return { cart: transformCart(res.data), message: res.message as string };
  });
};

export const getCartWidgets = (productIds: string[]) => {
  const url = `${getUrl(ROUTES.GET_CART_WIDGETS)}?product_ids=${productIds.join(',')}`;
  return ApiHelper(url).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.status !== 'success') {
      return Promise.reject(res);
    }

    return cartWidgetsTransformer(res);
  });
};

export const getCouponV2 = (couponId: number) => {
  const url = getUrl(ROUTES.GET_COUPON_V2);

  const params = new URLSearchParams();
  params.set('deviceType', getDeviceType());
  params.set('domain', getDomain());
  params.set('couponId', couponId.toString());

  return ApiHelper(`${url}?${params}`).then(async (response) => {
    if (response.ok === false) {
      return Promise.reject({ message: API_ERROR_MESSAGE });
    }

    const res = await response.json();

    if (res.statusCode !== '200') {
      return Promise.reject(res);
    }

    return res.data;
  });
};

export const setShippingAddressV2 = (addressId: string, source: string) => {
  const headers = { ...getEddHeader(), ...getONRHeader() };

  const body = {
    addressId,
    domain: 'fashion',
    deviceType: getDeviceType(),
    originSource: source,
  };

  const url = getUrl(ROUTES.ADD_ADDRESS_V2);

  return ApiHelper(url, {
    method: 'POST',
    body,
    headers: {
      countryCode: 'IN',
      currency: 'INR',
      Platform: getDeviceType(),
      ...headers,
    },
    isBodyJSON: true,
  })
    .then(async (response) => {
      if (!response.ok) {
        return Promise.reject(response);
      }
      const res = await response.json();

      if (source === EDD_SOURCE_PAGE.CART && res.statusCode === CART_ERROR_CODES.NOT_SERVICEABLE) {
        return {
          success: false,
          message: res?.message,
          statusCode: CART_ERROR_CODES.NOT_SERVICEABLE,
        };
      }

      if (res.statusCode !== '200') {
        return Promise.reject(res);
      }

      if (source !== EDD_SOURCE_PAGE.CART) {
        return {
          success: !res?.data?.error,
        };
      }
      return { cart: transformCart(res.data), message: res.message as string, success: true };
    })
    .catch(() => {
      log({
        errTitle: 'Error in cart set shipping addreess api',
        tags: { module: ERROR_TAGS.API_RESOLUTION },
        extra: { transformer: 'Cart setShippingV2 api' },
      });
      return Promise.reject({ statusCode: 500 });
    });
};
